import { yupResolver } from "@hookform/resolvers/yup";
import { isDefined } from "@omni/utils/helper.utils";
import { entries, fromEntries } from "@omni/utils/object.utils";
import { isEmpty } from "lodash-es";
import { Suspense, useEffect, useMemo, useState } from "react";

import { useOmniForm } from "@/components/Form/RHF/Utils/useOmniForm";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { ReviewSubmissionModel } from "@/types/service/reviews/reviewSubmission.model";
import { FormInfoProvider } from "@/utils/forms/formInfo.provider";
import { OmniFormContextProvider } from "@/utils/forms/omniForm.provider";

import { useDeviceFingerPrinting } from "../context/deviceFingerPrinting.context";
import { Step } from "./components/step/step.container";
import { HuboPartial } from "./partials/hubo";
import { MediaPartial } from "./partials/media";
import { ProductPartial } from "./partials/product";
import { ReviewPartial } from "./partials/review";
import {
    SwitchSteps,
    WriteInitialReviewValidationSchema,
    WriteProgressiveReviewFormValues,
    WriteProgressiveReviewValidationSchema
} from "./writeReview.schema";

export const FORM_ID = "writeReview";

const FIELD_ERROR_NAME_MAP: Partial<
    Record<keyof ReviewSubmissionModel, string>
> = {
    rating: "rating",
    secondaryRatings: "rating",
    wouldRecommend: "isRecommended"
};

const toFormError = ([field, message]: [
    keyof ReviewSubmissionModel,
    string | undefined
]) => {
    const formField = FIELD_ERROR_NAME_MAP[field] ?? field;

    return [formField, { message: () => () => message }] as const;
};

const stepsConfig: Array<{
    tk: string;
    optional: boolean;
    isComplete: (
        filledInFormValues: Set<keyof WriteProgressiveReviewFormValues>
    ) => boolean;
}> = [
    {
        tk: "product.reviews.writeReview.step.title.reviews",
        optional: false,
        isComplete: (filledInFormValues) => {
            return (
                filledInFormValues.has("rating") &&
                filledInFormValues.has("agreedToTermsAndConditions")
            );
        }
    },
    {
        tk: "product.reviews.writeReview.step.title.images",
        optional: true,
        isComplete: (filledInFormValues) => {
            return filledInFormValues.has("photos");
        }
    },
    {
        tk: "product.reviews.writeReview.step.title.productReview",
        optional: true,
        isComplete: (filledInFormValues) => {
            return (
                filledInFormValues.has("isRecommended") ||
                filledInFormValues.has("ratingQuality") ||
                filledInFormValues.has("ratingValue")
            );
        }
    },
    {
        tk: "product.reviews.writeReview.step.title.huboReview",
        optional: true,
        isComplete: (filledInFormValues) => {
            return filledInFormValues.has("brandScore");
        }
    }
];

export type WriteReviewComponentProps = {
    initialRating: number | null;
    reviewSubmission: ReviewSubmissionModel | null;
    reviewAuthor: string | null;
    fieldErrors: Partial<Record<keyof ReviewSubmissionModel, string>>;
    onSubmit: (value: ReviewSubmissionModel) => Promise<void>;
};

const WriteReviewComponent = ({
    initialRating,
    reviewSubmission,
    reviewAuthor,
    fieldErrors,
    onSubmit
}: WriteReviewComponentProps) => {
    const { t } = useGranite();
    const { loadDeviceFingerPrinting } = useDeviceFingerPrinting();

    const defaultFormValues = {
        rating: initialRating ?? reviewSubmission?.rating,
        title: reviewSubmission?.title ?? "",
        text: reviewSubmission?.text ?? "",
        nickname: reviewSubmission?.nickname ?? "",
        email: reviewSubmission?.email ?? "",
        agreedToTermsAndConditions:
            reviewSubmission?.agreedToTermsAndConditions ?? false,
        isRecommended: !isDefined(reviewSubmission?.wouldRecommend)
            ? SwitchSteps.NO_FEEDBACK
            : reviewSubmission.wouldRecommend
              ? SwitchSteps.YES
              : SwitchSteps.NO,
        brandScore: reviewSubmission?.brandScore,
        brandComment: reviewSubmission?.brandComment ?? "",
        ratingQuality: reviewSubmission?.secondaryRatings?.quality,
        ratingValue: reviewSubmission?.secondaryRatings?.value,
        photos: isEmpty(reviewSubmission?.photos)
            ? undefined
            : reviewSubmission?.photos?.map((photo) => ({
                  url: photo.url,
                  caption: photo.caption || undefined
              }))
    };

    const filledInFormValues = new Set(
        entries(defaultFormValues)
            .filter(([, value]) => {
                return (
                    isDefined(value) &&
                    value !== "" &&
                    value !== false &&
                    value !== SwitchSteps.NO_FEEDBACK
                );
            })
            .map(([key]) => key)
    );

    const { steps } = stepsConfig.reduceRight<{
        steps: Array<{
            tk: string;
            optional: boolean;
            isComplete: boolean;
            isSkipped: boolean;
        }>;
        isComplete: boolean;
    }>(
        (acc, step) => {
            const isComplete = step.isComplete(filledInFormValues);
            const isSkipped = !isComplete && acc.isComplete;

            const newStep = {
                ...step,
                isComplete: isComplete,
                isSkipped: isSkipped
            };

            return {
                steps: [newStep, ...acc.steps],
                isComplete: acc.isComplete || newStep.isComplete
            };
        },
        { steps: [], isComplete: false }
    );

    const [activeStepIndex, setActiveStepIndex] = useState(
        steps.findIndex((step) => !step.isComplete && !step.isSkipped)
    );

    const formErrors = useMemo(
        () => fromEntries(entries(fieldErrors).map(toFormError)),
        [fieldErrors]
    );

    const form = useOmniForm({
        mode: "onBlur",
        reValidateMode: "onBlur",
        errors: formErrors,
        defaultValues: { ...defaultFormValues },
        resolver: yupResolver(
            isDefined(reviewSubmission) || isDefined(reviewAuthor)
                ? WriteProgressiveReviewValidationSchema
                : WriteInitialReviewValidationSchema
        )
    });

    const {
        formState: { isSubmitting, submitCount }
    } = form;

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        form.reset({ ...defaultFormValues });
        setActiveStepIndex(
            steps.findIndex((step) => !step.isComplete && !step.isSkipped)
        );
    }, [reviewSubmission]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        if (isSubmitting || submitCount > 0) {
            loadDeviceFingerPrinting();
        }
    }, [isSubmitting, submitCount]);

    const handleOnSubmit = async (values: WriteProgressiveReviewFormValues) => {
        const ratingQuality = values.ratingQuality;
        const ratingValue = values.ratingValue;

        await onSubmit({
            agreedToTermsAndConditions: values.agreedToTermsAndConditions,
            rating: values.rating,
            email: values.email || undefined,
            title: values.title || undefined,
            text: values.text || undefined,
            nickname: values.nickname || undefined,
            wouldRecommend: {
                [SwitchSteps.NO]: false,
                [SwitchSteps.YES]: true,
                [SwitchSteps.NO_FEEDBACK]: undefined
            }[values.isRecommended ?? SwitchSteps.NO_FEEDBACK],
            ...(isDefined(ratingQuality) &&
                isDefined(ratingValue) && {
                    secondaryRatings: {
                        quality: ratingQuality,
                        value: ratingValue
                    }
                }),
            photos: values.photos?.map((photo) => ({
                url: photo.url,
                caption: photo.caption || undefined
            })),
            brandScore: values.brandScore || undefined,
            brandComment: values.brandComment || undefined
        });
    };

    const handleOnSkipStep = () => setActiveStepIndex((prev) => prev + 1);

    const createHandleOnEditStep = (index: number) => () =>
        setActiveStepIndex(index);

    return (
        <Suspense fallback={<p>{t("general.loading")}</p>}>
            <FormInfoProvider
                formId={FORM_ID}
                withNeverbounce={true}
            >
                {({ initialized }) => (
                    <OmniFormContextProvider {...form}>
                        <form
                            id={FORM_ID}
                            onSubmit={form.handleSubmit(handleOnSubmit)}
                            className={"mt-3"}
                        >
                            <Step
                                steps={steps}
                                stepIndex={0}
                                activeStepIndex={activeStepIndex}
                                onSkipStep={handleOnSkipStep}
                                onEditStep={createHandleOnEditStep(0)}
                            >
                                <ReviewPartial
                                    disabled={!initialized}
                                    authorDetails={
                                        !isDefined(reviewSubmission) &&
                                        !isDefined(reviewAuthor)
                                    }
                                />
                            </Step>
                            <Step
                                steps={steps}
                                stepIndex={1}
                                activeStepIndex={activeStepIndex}
                                onSkipStep={handleOnSkipStep}
                                onEditStep={createHandleOnEditStep(1)}
                            >
                                <MediaPartial
                                    disabled={!initialized}
                                    staticPhotoUrls={
                                        reviewSubmission?.photos?.map(
                                            (photo) => photo.url
                                        ) ?? []
                                    }
                                />
                            </Step>
                            <Step
                                steps={steps}
                                stepIndex={2}
                                activeStepIndex={activeStepIndex}
                                onSkipStep={handleOnSkipStep}
                                onEditStep={createHandleOnEditStep(2)}
                            >
                                <ProductPartial disabled={!initialized} />
                            </Step>
                            <Step
                                steps={steps}
                                stepIndex={3}
                                activeStepIndex={activeStepIndex}
                                onSkipStep={handleOnSkipStep}
                                onEditStep={createHandleOnEditStep(3)}
                            >
                                <HuboPartial disabled={!initialized} />
                            </Step>
                        </form>
                    </OmniFormContextProvider>
                )}
            </FormInfoProvider>
        </Suspense>
    );
};

export { WriteReviewComponent };
